import { useState, type FormEvent } from 'react';
import DatePopover from '@/components/DatePopover';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { orgs } from '@/api/catalogs';
import {
  employees,
  individuals,
  positionsApi,
  departmentsApi,
  workSchedulesApi,
  calcTypesApi,
  EMPLOYMENT_TYPE_LABELS,
  EMPLOYMENT_KIND_LABELS,
  LABOR_CONTRACT_TYPE_LABELS,
  type EmploymentType,
  type EmploymentKind,
  type EmployeeWithDocumentCreate,
} from '@/api/hr';

/**
 * Єдина форма створення працівника (UX-покращення проти 1С-стилю).
 *
 * Один екран: фізособа (нова АБО обрана зі списку) + тип (штат/ЦПХ/без договору)
 * → поля → працівник + наказ про прийом / договір ЦПХ однією атомарною дією.
 * Поля за еталоном 1С: графік роботи, вид розрахунку, вид трудового договору,
 * строк дії, підрозділ/срок оплати ЦПХ. Табельний — автонумерація (порожнє).
 */
export default function EmployeeWithDocumentPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const posList = useQuery({ queryKey: ['positions'], queryFn: () => positionsApi.list({ limit: 500 }) });
  const schedList = useQuery({ queryKey: ['work-schedules'], queryFn: () => workSchedulesApi.list() });
  const calcList = useQuery({ queryKey: ['calc-types'], queryFn: calcTypesApi.list });
  const indList = useQuery({ queryKey: ['individuals-all'], queryFn: () => individuals.list({ limit: 1000 }) });

  // ── Шапка ──
  const [orgId, setOrgId] = useState('');
  const [type, setType] = useState<EmploymentType>('STAFF');
  const [employmentKind, setEmploymentKind] = useState<EmploymentKind>('MAIN');
  const [code, setCode] = useState('');

  // ── Фізособа: лише вибір зі списку (ручне створення — на сторінці Фізособи) ──
  const [existingIndId, setExistingIndId] = useState('');

  // ── Наказ (STAFF) ──
  const [laborType, setLaborType] = useState('PERMANENT');
  const [hireDate, setHireDate] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [positionId, setPositionId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [scheduleId, setScheduleId] = useState('');
  const [calcTypeId, setCalcTypeId] = useState('');
  const [salary, setSalary] = useState('');
  const [probationDays, setProbationDays] = useState('');
  const [basis, setBasis] = useState('');  // Підстава (contract_basis наказу)

  // Підрозділи ТIЛЬКИ обраної юрособи — інакше можна було обрати чужий підрозділ,
  // і він не підтягувався у наказ (наказ-редактор фільтрує депи по своїй org).
  const depList = useQuery({
    queryKey: ['departments-by-org', orgId],
    queryFn: () => departmentsApi.list({ organization_id: orgId, limit: 500 }),
    enabled: !!orgId,
  });

  // ── Договір (ЦПХ) ──
  const [subject, setSubject] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amount, setAmount] = useState('');
  const [civilDeptId, setCivilDeptId] = useState('');
  // Вид розрахунку ЦПХ: разово / подобово / погодинно.
  const [civilCalcType, setCivilCalcType] = useState<'ONCE' | 'DAILY' | 'HOURLY'>('ONCE');
  const [paymentTerm, setPaymentTerm] = useState('');

  const mut = useMutation({
    mutationFn: (p: EmployeeWithDocumentCreate) => employees.createWithDocument(p),
    onSuccess: (res) => {
      setError(null);
      // Редірект на створений документ (запит власника 2026-06-15):
      // трудовий → наказ прийому; ЦПХ → договір; інакше (NO_CONTRACT) → картка.
      if (res.document_type === 'HIRE_ORDER' && res.document_id) {
        navigate(`/hire-orders/${res.document_id}`);
      } else if (res.document_type === 'CIVIL_LAW' && res.document_id) {
        navigate(`/civil-law-contracts/${res.document_id}`);
      } else {
        navigate(`/employees/${res.employee_id}`);
      }
    },
    onError: (e) => {
      const err = e as AxiosError<{ detail?: unknown }>;
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Не вдалося створити працівника.');
    },
  });

  function submit(e: FormEvent, post: boolean) {
    e.preventDefault();
    setError(null);
    const payload: EmployeeWithDocumentCreate = {
      organization_id: orgId,
      employment_type: type,
      employment_kind: employmentKind,
      code: code.trim() || null,
      post,
    };
    payload.individual_id = existingIndId;
    if (type === 'STAFF') {
      payload.labor_contract_type = laborType;
      payload.hire_order = {
        // Дата наказу = дата СТВОРЕННЯ (сьогодні), а не дата прийому (hireDate).
        // Час не потрібен — лише дата (рішення власника 2026-06-15).
        document_date: `${new Date().toISOString().slice(0, 10)}T00:00:00Z`,
        hire_date: hireDate,
        position_id: positionId || null,
        department_id: departmentId || null,
        work_schedule_id: scheduleId || null,
        calc_type_id: calcTypeId || null,
        contract_valid_until: validUntil || null,
        salary_amount: Number(salary) || 0,
        probation_days: probationDays ? Number(probationDays) : null,
        contract_basis: basis.trim() || null,
      };
    } else if (type === 'CIVIL_LAW') {
      payload.civil_law = {
        document_date: `${dateFrom}T00:00:00Z`,
        contract_subject: subject.trim() || null,
        date_from: dateFrom,
        date_to: dateTo || null,
        total_amount: Number(amount) || 0,
        calc_type: civilCalcType,
        department_id: civilDeptId || null,
        payment_term: paymentTerm.trim() || null,
      };
    }
    mut.mutate(payload);
  }

  const indValid = !!existingIndId;
  const docValid =
    type === 'NO_CONTRACT' ||
    (type === 'STAFF' && hireDate) ||
    (type === 'CIVIL_LAW' && dateFrom);
  const canSubmit = orgId && indValid && docValid && !mut.isPending;

  // Погодинна: підпис поля «Тариф/оклад» → «Ставка за годину» (alembic 0291).
  const isHourly = (calcList.data ?? []).some(
    (c) => c.id === calcTypeId && c.code === 'HOURLY',
  );

  return (
    <div>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Новий працівник</h2>
        {/* Кнопки вгорі — спільний з усіма наказами набір (запит власника).
            Прийом штатного = створення працівника, тож тут той самий патерн:
            Скасувати / Зберегти чернетку / Зберегти та провести. Для «без договору»
            проведення немає — лише «Створити». */}
        <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
          <button type="button" onClick={() => navigate('/hr-orders')}>Скасувати</button>
          {type === 'NO_CONTRACT' ? (
            <button type="button" className="primary" disabled={!canSubmit}
                    onClick={(e) => submit(e, false)}>
              {mut.isPending ? 'Збереження…' : 'Створити'}
            </button>
          ) : (
            <>
              <button type="button" disabled={!canSubmit} onClick={(e) => submit(e, false)}>
                {mut.isPending ? 'Збереження…' : 'Зберегти чернетку'}
              </button>
              <button type="button" className="primary" disabled={!canSubmit}
                      onClick={(e) => submit(e, true)}>
                {mut.isPending ? 'Збереження…' : 'Зберегти та провести'}
              </button>
            </>
          )}
        </div>
      </div>

      <form className="card">
        {error && <div className="error" style={{ marginBottom: '1rem' }}>{error}</div>}

        {/* Організація + Табельний № + Вид зайнятості — в одну строку */}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div className="field" style={{ width: 220, flex: '0 0 auto' }}>
            <label>Організація*</label>
            <select value={orgId}
                    onChange={(e) => {
                      setOrgId(e.target.value);
                      // Скинути обрані підрозділи — вони належали попередній юрособі.
                      setDepartmentId('');
                      setCivilDeptId('');
                    }}
                    required>
              <option value="">— оберіть —</option>
              {(orgList.data?.items ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div className="field" style={{ width: 160, flex: '0 0 auto' }}>
            <label>Табельний №</label>
            <input value={code} onChange={(e) => setCode(e.target.value)}
                   placeholder="авто, якщо порожнє" />
          </div>
          <div className="field" style={{ flex: '0 0 auto' }}>
            <label>Вид зайнятості*</label>
            <div style={{ display: 'flex', gap: '1.25rem', minHeight: 38, alignItems: 'center' }}>
              {/* «Без договору» (NO_CONTRACT) приховано з вибору — рідкісний
                  інформаційний вид. Код-гілка NO_CONTRACT лишається для сумісності. */}
              {(['STAFF', 'CIVIL_LAW'] as EmploymentType[]).map((t) => (
                <label key={t} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <input type="radio" checked={type === t} style={{ width: 'auto' }} onChange={() => setType(t)} />
                  {EMPLOYMENT_TYPE_LABELS[t]}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* ── Фізособа: лише вибір зі списку + посилання «Створити» одразу за заголовком ── */}
        <div className="field" style={{ marginTop: '1.25rem' }}>
          <label style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem' }}>
            Фізособа*
            <a href="/individuals" target="_blank" rel="noreferrer"
               style={{ fontSize: '0.82rem', fontWeight: 'normal' }}>
              створити ↗
            </a>
          </label>
          <select value={existingIndId} onChange={(e) => setExistingIndId(e.target.value)} required>
            <option value="">— оберіть —</option>
            {(indList.data?.items ?? []).map((i) => (
              <option key={i.id} value={i.id}>
                {[i.last_name, i.first_name, i.middle_name].filter(Boolean).join(' ')} {i.inn ? `(${i.inn})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* ── Трудовий договір (STAFF) ── */}
        {type === 'STAFF' && (
          <>
            <h3 style={{ marginTop: '1.25rem' }}>Трудовий договір (наказ про прийом)</h3>
            {/* Вид зайнятості + Вид договору + Дата прийому + Діє по + Випроб. — одна строка. */}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div className="field" style={{ minWidth: 200 }}>
                <label>Вид зайнятості</label>
                <select value={employmentKind}
                        onChange={(e) => setEmploymentKind(e.target.value as EmploymentKind)}>
                  {(Object.entries(EMPLOYMENT_KIND_LABELS) as Array<[EmploymentKind, string]>)
                    .map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="field" style={{ minWidth: 180 }}>
                <label>Вид договору</label>
                <select value={laborType} onChange={(e) => setLaborType(e.target.value)}>
                  {Object.entries(LABOR_CONTRACT_TYPE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Дата прийому*</label>
                <DatePopover value={hireDate} onChange={(v) => setHireDate(v)} />
              </div>
              {(laborType === 'CONTRACT' || laborType === 'FIXED_TERM') && (
                <div className="field">
                  <label>Діє по</label>
                  <DatePopover value={validUntil} onChange={(v) => setValidUntil(v)} />
                </div>
              )}
              <div className="field">
                <label>Випроб. (днів)</label>
                <input type="number" min={0} value={probationDays} onChange={(e) => setProbationDays(e.target.value)} style={{ width: 110 }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div className="field" style={{ flex: 1, minWidth: 180 }}>
                <label style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem' }}>
                  Підрозділ
                  <a href="/departments" target="_blank" rel="noreferrer"
                     style={{ fontSize: '0.82rem', fontWeight: 'normal' }}>створити ↗</a>
                </label>
                <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                  <option value="">— не вказано —</option>
                  {(depList.data?.items ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div className="field" style={{ flex: 1, minWidth: 180 }}>
                <label style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem' }}>
                  Посада
                  <a href="/positions" target="_blank" rel="noreferrer"
                     style={{ fontSize: '0.82rem', fontWeight: 'normal' }}>створити ↗</a>
                </label>
                <select value={positionId} onChange={(e) => setPositionId(e.target.value)}>
                  <option value="">— не вказано —</option>
                  {(posList.data?.items ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div className="field" style={{ minWidth: 170 }}>
                <label>Графік роботи</label>
                <select value={scheduleId} onChange={(e) => setScheduleId(e.target.value)}>
                  <option value="">— не вказано —</option>
                  {/* Шестиденку приховано з вибору (рішення власника). */}
                  {(schedList.data ?? [])
                    .filter((s) => s.name !== 'Шестиденний робочий тиждень')
                    .map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="field" style={{ minWidth: 170 }}>
                <label>Вид розрахунку</label>
                <select value={calcTypeId} onChange={(e) => setCalcTypeId(e.target.value)}>
                  <option value="">— не вказано —</option>
                  {/* Відрядна (PIECEWORK) і За середнім (AVERAGE) приховано: рушія
                      їх розрахунку ще немає — формула рахує лише ставка×дні
                      (TECHDEBT TD-PAYROLL-CALCTYPE). Лишаємо Оклад/Погодинну. */}
                  {(calcList.data ?? [])
                    .filter((c) => c.code !== 'PIECEWORK' && c.code !== 'AVERAGE')
                    .map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="field">
                <label>{isHourly ? 'Ставка за годину, грн' : 'Тариф/оклад, грн'}</label>
                <input type="number" step="0.01" value={salary} onChange={(e) => setSalary(e.target.value)} style={{ width: 150 }} />
              </div>
            </div>
            {/* Підстава наказу (contract_basis) — на всю ширину. */}
            <div className="field">
              <label>Підстава</label>
              <input value={basis} onChange={(e) => setBasis(e.target.value)}
                     placeholder="наприклад: Трудовий договір №1 від 15.01.2026" />
            </div>
          </>
        )}

        {/* ── Договір ЦПУ ── */}
        {type === 'CIVIL_LAW' && (
          <>
            <h3 style={{ marginTop: '1.25rem' }}>Договір ЦПУ</h3>
            {/* Предмет договору — на всю ширину. */}
            <div className="field">
              <label>Предмет договору</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
            {/* Дата + Підрозділ в одній строці. */}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div className="field">
                <label>Діє з*</label>
                <DatePopover value={dateFrom} onChange={(v) => setDateFrom(v)} />
              </div>
              <div className="field">
                <label>Діє до</label>
                <DatePopover value={dateTo} onChange={(v) => setDateTo(v)} />
              </div>
              <div className="field" style={{ flex: 1, minWidth: 180 }}>
                <label>Підрозділ</label>
                <select value={civilDeptId} onChange={(e) => setCivilDeptId(e.target.value)}>
                  <option value="">— не вказано —</option>
                  {(depList.data?.items ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div className="field">
                <label>Вид розрахунку</label>
                <select value={civilCalcType}
                        onChange={(e) => setCivilCalcType(e.target.value as 'ONCE' | 'DAILY' | 'HOURLY')}>
                  <option value="ONCE">разово</option>
                  <option value="DAILY">подобово</option>
                  <option value="HOURLY">погодинно</option>
                </select>
              </div>
              <div className="field">
                <label>Розмір, грн{civilCalcType === 'DAILY' ? ' (за день)'
                  : civilCalcType === 'HOURLY' ? ' (за годину)' : ''}</label>
                <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} style={{ width: 140 }} />
              </div>
              <div className="field" style={{ flex: 1, minWidth: 200 }}>
                <label>Строк оплати</label>
                <input value={paymentTerm} onChange={(e) => setPaymentTerm(e.target.value)}
                       placeholder="напр. щомісячно / по закінченню / свій варіант" />
              </div>
            </div>
          </>
        )}

        {type === 'NO_CONTRACT' && (
          <p style={{ color: 'var(--muted)', marginTop: '1rem' }}>
            Тип «без договору» — створюється лише картка працівника, без документа.
          </p>
        )}
      </form>
    </div>
  );
}
